import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Grid,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  isUsernameAvailable,
  isEmailAvailable,
  clearUserState,
} from "../../State/User/Action";
import {
  clearAuthState,
  registerUser,
  sendEmailOtp,
  verifyEmail,
} from "../../State/Auth/Action";

const Register = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState("ROLE_LEARNER");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");

  const [userIdStatus, setUserIdStatus] = useState("");
  const [isUserIdValid, setIsUserIdValid] = useState(false);

  const [emailStatus, setEmailStatus] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const {
    isUsernameAvailableResult,
    isUsernameAvailableLoading,
    isUsernameAvailableError,
    isEmailAvailableResult,
    isEmailAvailableLoading,
    isEmailAvailableError,
  } = useSelector((state) => state.user);

  const {
    emailVerificationResult,
    emailVerificationLoading,
    emailVerificationError,
    registerRequestResult,
    registerRequestLoading,
    registerRequestError,
  } = useSelector((state) => state.auth);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userIdRegex = /^[a-zA-Z0-9]{6,20}$/;
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[&@$#])[A-Za-z\d&@$#]{6,20}$/;

  useEffect(() => {
    dispatch(clearUserState());
    dispatch(clearAuthState());
  }, [dispatch]);

  useEffect(() => {
    const validateUserId = () => {
      if (userId.length >= 6 && userIdRegex.test(userId)) {
        dispatch(isUsernameAvailable(userId));
      } else {
        setUserIdStatus("");
        setIsUserIdValid(false);
      }
    };
    validateUserId();
  }, [userId, dispatch]);

  useEffect(() => {
    if (isUsernameAvailableLoading) {
      setUserIdStatus("Checking availability...");
      setIsUserIdValid(false);
    } else if (isUsernameAvailableError) {
      setUserIdStatus("Error checking availability");
      setIsUserIdValid(false);
    } else if (isUsernameAvailableResult.data === true) {
      setUserIdStatus("User ID is available");
      setIsUserIdValid(true);
    } else if (isUsernameAvailableResult.data === false) {
      setUserIdStatus("User ID is already taken");
      setIsUserIdValid(false);
    }
  }, [
    isUsernameAvailableResult,
    isUsernameAvailableLoading,
    isUsernameAvailableError,
  ]);

  useEffect(() => {
    const validateEmail = () => {
      if (email && email.includes("@")) {
        dispatch(isEmailAvailable(email));
      } else {
        setEmailStatus("");
        setIsEmailValid(false);
        dispatch(clearUserState());
      }
    };
    validateEmail();
  }, [email, dispatch]);

  useEffect(() => {
    if (isEmailAvailableLoading) {
      setEmailStatus("Checking email...");
      setIsEmailValid(false);
    } else if (isEmailAvailableError) {
      setEmailStatus("Error checking email");
      setIsEmailValid(false);
    } else if (isEmailAvailableResult.data === true) {
      setEmailStatus("Email is available");
      setIsEmailValid(true);
    } else if (isEmailAvailableResult.data === false) {
      setEmailStatus("Email is already in use");
      setIsEmailValid(false);
    }
  }, [isEmailAvailableResult, isEmailAvailableLoading, isEmailAvailableError]);

  const handleVerifyEmail = () => {
    if (!email) {
      toast.error("Please enter an email first");
      return;
    }
    dispatch(sendEmailOtp(email));
    setOtpDialogOpen(true);
    setOtpErrorMessage("");
    toast.success("OTP sent to email!");
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      setOtpErrorMessage("Please enter a valid 4-digit OTP");
      return;
    }
    dispatch(verifyEmail({ email, otp: otpValue }));
  };

  useEffect(() => {
    if (emailVerificationError) {
      setOtpErrorMessage("Invalid OTP. Please try again.");
      setIsEmailVerified(false);
    } else if (emailVerificationResult?.data === true) {
      setIsEmailVerified(true);
      setOtpDialogOpen(false);
      toast.success("Email verified successfully!");
      setOtpErrorMessage("");
    } else if (emailVerificationResult?.data === false) {
      setOtpErrorMessage("Invalid OTP. Please try again.");
      setIsEmailVerified(false);
    }
    dispatch(clearAuthState());
  }, [
    dispatch,
    emailVerificationResult,
    emailVerificationLoading,
    emailVerificationError,
  ]);

  const handleRegister = async () => {
    setErrorMessage("");

    if (!firstName || firstName.length < 2 || firstName.length > 20) {
      setErrorMessage(
        "Please enter a valid First Name (min 2, max 20 characters)"
      );
      return;
    }
    if (!lastName || lastName.length < 2 || lastName.length > 20) {
      setErrorMessage(
        "Please enter a valid Last Name (min 2,  max 20 characters)"
      );
      return;
    }
    if (!email || !userId || !password || !confirmPassword) {
      setErrorMessage("Please fill all fields");
      return;
    }

    if (email.length < 5 || !email.includes("@")) {
      setErrorMessage("Please enter a valid Email");
      return;
    }

    if (email.length > 50) {
      setErrorMessage("Email must be at most 50 characters");
      return;
    }

    if (userId.length > 20) {
      setErrorMessage("User ID must be at most 20 characters");
      return;
    }
    if (!isEmailVerified) {
      setErrorMessage("Please verify your email");
      return;
    }
    if (!userIdRegex.test(userId) || !isUserIdValid) {
      setErrorMessage("Please enter a valid and available User ID");
      return;
    }
    if (!isEmailValid) {
      setErrorMessage("Please enter a valid and available Email");
      return;
    }
    if (!passwordRegex.test(password)) {
      setErrorMessage(
        "Password must be 6-20 characters and include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (&@$#)"
      );
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }

    var payload = {
      firstName,
      lastName,
      email,
      username: userId,
      role,
      password,
    };

    dispatch(registerUser(payload));
  };

  useEffect(() => {
    if (registerRequestError?.success === false) {
      setErrorMessage(registerRequestError.message);
    } else if (registerRequestResult?.data?.success === true) {
      toast.success("Registration successful!");
      navigate("/login");
    } else if (registerRequestResult?.data?.success === false) {
      setErrorMessage("Registration failed. Please try again.");
    }
    dispatch(clearAuthState());
  }, [
    dispatch,
    registerRequestResult,
    registerRequestLoading,
    registerRequestError,
    navigate,
  ]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Card className="w-full max-w-2xl p-10 rounded-3xl shadow-2xl bg-white">
          <CardContent className="flex flex-col gap-6">
            <Typography
              variant="h4"
              align="center"
              fontWeight="bold"
              gutterBottom
            >
              Create Your Account
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <TextField
                    label="First Name"
                    variant="outlined"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                  />
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <motion.div whileFocus={{ scale: 1.02 }}>
                  <TextField
                    label="Last Name"
                    variant="outlined"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                  />
                </motion.div>
              </Grid>
            </Grid>

            <motion.div whileFocus={{ scale: 1.02 }}>
              <TextField
                label="Email"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                  endAdornment: isEmailVerified ? (
                    <VerifiedIcon color="success" />
                  ) : isEmailValid ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={handleVerifyEmail}
                      style={{
                        marginRight: "5px",
                        fontSize: "12px",
                        textTransform: "none",
                      }}
                    >
                      Verify
                    </Button>
                  ) : null,
                }}
              />
              {emailStatus && (
                <Typography
                  variant="caption"
                  style={{
                    color: isEmailValid ? "green" : "red",
                    marginLeft: "10px",
                    marginTop: "4px",
                  }}
                >
                  {emailStatus}
                </Typography>
              )}
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }}>
              <TextField
                label="User ID"
                variant="outlined"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        userId.length >= 6
                          ? isUserIdValid
                            ? "green"
                            : "red"
                          : undefined,
                    },
                  },
                }}
              />
              {userIdStatus && (
                <Typography
                  variant="caption"
                  style={{
                    color: isUserIdValid ? "green" : "red",
                    marginLeft: "10px",
                    marginTop: "4px",
                  }}
                >
                  {userIdStatus}
                </Typography>
              )}
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }}>
              <TextField
                select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
              >
                <MenuItem value="ROLE_INSTRUCTOR">Instructor</MenuItem>
                <MenuItem value="ROLE_LEARNER">Learner</MenuItem>
              </TextField>
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }}>
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }}>
              <TextField
                label="Confirm Password"
                variant="outlined"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>

            <motion.div whileHover={{ scale: 1.03 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleRegister}
                style={{
                  fontSize: "18px",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                }}
                startIcon={<CheckCircleIcon />}
              >
                Register
              </Button>
            </motion.div>

            {errorMessage && (
              <Typography className="text-red-500 text-center mt-2">
                {errorMessage}
              </Typography>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <Dialog
        open={otpDialogOpen}
        onClose={() => {
          setOtpDialogOpen(false);
          setOtpErrorMessage("");
        }}
      >
        <DialogTitle>Verify Email OTP</DialogTitle>
        <DialogContent className="flex flex-col items-center">
          <Typography className="mb-4 text-gray-700">
            Enter the 4-digit OTP sent to your email
          </Typography>
          <div className="flex gap-2">
            {otp.map((digit, index) => (
              <TextField
                key={index}
                id={`otp-${index}`}
                variant="outlined"
                type="tel"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "20px",
                    width: "50px",
                  },
                }}
              />
            ))}
          </div>
          {otpErrorMessage && (
            <Typography className="text-red-500 mt-2">
              {otpErrorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOtpDialogOpen(false);
              setOtpErrorMessage("");
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVerifyOtp}>
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Register;
